if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        
        // Start every product on "select an option"; a short delay lets
        // product-form finish upgrading first.
        setTimeout(() => {
          this.setInitialButtonState();
        }, 10);
        
        // Listen for auto-selection events
        this.addEventListener('auto-selection-completed', this.handleAutoSelectionCompleted.bind(this));
        
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      // The shopper confirms a variant before it can be added, even when the
      // server rendered a selection.
      setInitialButtonState() {
        const productForm = this.productForm;
        if (productForm && typeof productForm.setSubmitState === 'function') {
          productForm.setSubmitState('select', { announce: false });
          
          // Clear any initially selected variant
          const variantInput = document.querySelector('input[name="id"]');
          if (variantInput && variantInput.value) {
            variantInput.value = '';
            variantInput.disabled = true;
          }
        }
      }

      // Handle auto-selection completion
      handleAutoSelectionCompleted(event) {
        // Check if all fieldsets have an option selected
        const allFieldsets = this.querySelectorAll('fieldset');
        let allOptionsSelected = true;

        allFieldsets.forEach(fieldset => {
          // Check for checked radio buttons or checkboxes
          const checkedInput = fieldset.querySelector('input:checked');
          // Check for selected dropdown options
          const select = fieldset.querySelector('select');
          const hasSelectSelection = select && select.value.trim() !== ''; // Check if the selected option has a non-empty value
          
          if (!checkedInput && !hasSelectSelection) {
            allOptionsSelected = false;
          }
        });

        if (allOptionsSelected) {
          // Enable the button since all options are now selected
          const productForm = this.productForm;
          if (productForm && typeof productForm.setSubmitState === 'function') {
            productForm.setSubmitState('ready', { announce: false });
          }
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, selectedVariant } }) {
        // Skip if we're currently restoring selections to prevent infinite loops
        if (this.isRestoringSelections) {
          return;
        }
        
        if (!this.contains(event.target)) {
          return;
        }

        // Check if we have a split picker with both options selected
        const hasSplitPicker = document.querySelector('.waist-option, .inseam-option');
        if (hasSplitPicker) {
          const waistChecked = document.querySelector('.waist-option:checked');
          const inseamChecked = document.querySelector('.inseam-option:checked');
          
          // If split picker is not fully selected, don't proceed with variant change
          if (!waistChecked || !inseamChecked) {
            return;
          }
        }

        // Store the client-side selectedVariant for later use
        this.lastSelectedVariant = selectedVariant;

        // Debounce rapid updates to prevent jumpiness
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
        }

        this.updateTimeout = setTimeout(() => {
          this.resetProductFormState();

          // Check if we have a split picker
          const hasSplitPicker = document.querySelector('.waist-option, .inseam-option');
          
          if (hasSplitPicker) {
            // For split pickers, handle updates directly without server request
            this.handleSplitPickerUpdate(selectedVariant);
                     } else {
             // Use the native variant selection logic for regular products
             
             const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
             this.pendingRequestUrl = productUrl;
             const shouldSwapProduct = this.dataset.url !== productUrl;
             const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

             // Pass variant ID if available for more reliable pricing updates
             const variantId = selectedVariant?.id || null;
             
             this.renderProductInfo({
               requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage, variantId),
               targetId: target.id,
               event: event,
               callback: shouldSwapProduct
                 ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
                 : this.handleUpdateProductInfo(productUrl),
             });
           }
        }, 100); // Wait 100ms before updating
      }

      resetProductFormState() {
        this.productForm?.setSubmitState('pending');
        this.productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
                     const variant = this.getSelectedVariant(html.querySelector(selector));
           this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, event, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            // Skip focus if this was triggered by auto-selection
            if (targetId && typeof targetId === "string" && targetId.trim() !== "" && !event?.isAutoSelection) {
              document.querySelector(`#${targetId}`)?.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              // The new selection never resolved and the hidden id still names
              // the previous variant, so clear it and settle on unavailable
              // rather than re-enable Add to Cart for the wrong variant.
              this.updateVariantInputs(null);
              this.productForm?.setSubmitState('unavailable');
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        // Use variant ID if available (more reliable for pricing), otherwise fall back to option_values
        if (variantId) {
          params.push(`variant=${variantId}`);
        } else if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        const hasSplitPicker = document.querySelector('.waist-option, .inseam-option');
        
        if (variantSelects && !hasSplitPicker) {
          // For regular products, preserve user selections before updating
          const currentSelections = this.preserveVariantSelections();
          
          // Update the variant selector
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          
          // Restore user selections after update
          this.restoreVariantSelections(currentSelections);
        }
        // For split pickers, skip variant selector update entirely to prevent DOM re-rendering
      }
      
      // NEW: Preserve variant selections before DOM update
      preserveVariantSelections() {
        const selections = {};
        const variantSelects = this.querySelector('variant-selects');
        
        if (!variantSelects) return selections;
        
        // Get all radio buttons and selects
        const radioButtons = variantSelects.querySelectorAll('input[type="radio"]:checked');
        const selects = variantSelects.querySelectorAll('select');
        
        // Store radio button selections
        radioButtons.forEach(radio => {
          const name = radio.name;
          if (!selections[name]) selections[name] = {};
          selections[name].type = 'radio';
          selections[name].value = radio.value;
        });
        
        // Store select selections
        selects.forEach(select => {
          const name = select.name;
          if (!selections[name]) selections[name] = {};
          selections[name].type = 'select';
          selections[name].value = select.value;
        });
        
        return selections;
      }
      
      // NEW: Restore variant selections after DOM update
      restoreVariantSelections(selections) {
        if (!selections || Object.keys(selections).length === 0) return;
        
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;
        
        // Set flag to prevent infinite loops
        this.isRestoringSelections = true;
        
        // Restore radio button selections
        Object.entries(selections).forEach(([name, data]) => {
          if (data.type === 'radio') {
            // Escape the name and value for CSS selector
            const escapedName = CSS.escape(name);
            const escapedValue = CSS.escape(data.value);
            const radio = variantSelects.querySelector(`input[name="${escapedName}"][value="${escapedValue}"]`);
            if (radio) {
              radio.checked = true;
              // Don't trigger change event during restoration to prevent infinite loops
            }
          } else if (data.type === 'select') {
            // Escape the name for CSS selector
            const escapedName = CSS.escape(name);
            const select = variantSelects.querySelector(`select[name="${escapedName}"]`);
            if (select) {
              select.value = data.value;
              // Don't trigger change event during restoration to prevent infinite loops
            }
          }
        });
        
        // Clear the flag after a short delay to allow DOM to settle
        setTimeout(() => {
          this.isRestoringSelections = false;
        }, 100);
      }

      // NEW: Selective element updates for split pickers
      updateElementsSelectively(html) {
        const hasSplitPicker = document.querySelector('.waist-option, .inseam-option');
        
        if (!hasSplitPicker) return; // Only for split pickers
        
        const updateElement = (elementId) => {
          const newElement = html.getElementById(`${elementId}-${this.sectionId}`);
          const currentElement = this.querySelector(`#${elementId}-${this.dataset.section}`);
          
          // For price elements, also find mobile variant
          const mobileElement = elementId === 'price' 
            ? document.querySelector(`#${elementId}-${this.dataset.section}--mobile`) 
            : null;
          
          // Collect all elements to update
          const elementsToUpdate = [currentElement, mobileElement].filter(Boolean);
          
          if (newElement && elementsToUpdate.length > 0) {
            elementsToUpdate.forEach((element) => {
              // Add loading state
              element.classList.add('updating');
              
              // Update content
              element.innerHTML = newElement.innerHTML;
              
              // Remove loading state after a short delay
              setTimeout(() => {
                element.classList.remove('updating');
              }, 100);
            });
          }
        };
        
        // Update only the elements that need to change
        updateElement('price');
        updateElement('Inventory');
        updateElement('Sku');
        updateElement('Volume');
        updateElement('Price-Per-Item');
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          // Check if we have a split picker
          const hasSplitPicker = document.querySelector('.waist-option, .inseam-option');
          
          let variant;
          if (hasSplitPicker) {
            // For split pickers, use the client-side determined variant
            variant = this.lastSelectedVariant || this.getSelectedVariant(html);
            // Clear the stored variant to prevent stale data
            this.lastSelectedVariant = null;
          } else {
            // For regular products, always use the server-determined variant
            variant = this.getSelectedVariant(html);
            
            // Check if the server returned a different variant than what was requested
            const requestedVariant = this.lastSelectedVariant;
            if (requestedVariant && variant && requestedVariant.id !== variant.id) {
              // If server returned a different variant, it means the requested combination is unavailable
              variant = null;
            }
          }

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (hasSplitPicker) {
            // For split pickers, update media directly from client-side variant data
            this.updateMediaForSplitPicker(variant?.featured_media?.id);
          } else {
            // For regular products, use the server HTML
            this.updateMedia(html, variant?.featured_media?.id);
          }

          if (hasSplitPicker) {
            // Use selective updates for split pickers to prevent jumpiness
            this.updateElementsSelectively(html);
          } else {
            // Use original update logic for regular variant selectors
            const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
              const sourceId = `${id}-${this.sectionId}`;
              const destinationId = `${id}-${this.dataset.section}`;
              const source = html.getElementById(sourceId);
              
              if (!source) {
                return;
              }
              
              // Find main destination element
              const mainDestination = document.querySelector(`#${destinationId}`);
              
              // For price elements, also find mobile variant
              const mobileDestination = id === 'price' 
                ? document.querySelector(`#${destinationId}--mobile`) 
                : null;
              
              // Collect all destinations to update
              const allDestinations = [mainDestination, mobileDestination].filter(Boolean);
              
              if (allDestinations.length === 0) {
                return;
              }
              
              // Update ALL destination elements
              allDestinations.forEach((destination) => {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              });
            };

            // Use enhanced native method that handles multiple elements
            updateSourceFromDestination('price');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // Button state. The split picker resolves its variant client-side,
          // so an empty id input means the waist/inseam pair isn't complete.
          const variantInput = document.querySelector('input[name="id"]');
          const hasVariantSelected = variantInput && variantInput.value;

          if (hasSplitPicker && !hasVariantSelected) {
            this.productForm?.setSubmitState('select');
          } else if (variant.available) {
            this.productForm?.setSubmitState('ready');
          } else {
            this.productForm?.setSubmitState('sold-out');
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
          
          // Remove the setTimeout calls since we're no longer doing full DOM updates
          if (window.initSplitVariantLogic) {
            window.initSplitVariantLogic();
          }
          if (window.initRegularVariantLogic) {
            window.initRegularVariantLogic();
          }
          if (window.initATCButtonStateManagement) {
            window.initATCButtonStateManagement();
          }
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (variantId) {
            input.value = variantId;
            input.disabled = false;
          } else {
            input.value = '';
            input.disabled = true;
          }
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        // Check if URL updates are disabled via theme setting
        const updateUrlOnVariantChange = window.themeSettings?.update_url_on_variant_change;
        if (updateUrlOnVariantChange === false) return;
        
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.setSubmitState('unavailable');

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      updateMediaForSplitPicker(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) {
          return;
        }

        // For split pickers, we need to make a minimal server request to get the correct media information
        // This ensures we get the proper variant_images for the selected variant
        const variantId = this.querySelector('input[name="id"]')?.value;
        if (!variantId) {
          return;
        }

        // Make a minimal request to get the media gallery HTML for this variant
        const requestUrl = `${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`;

        fetch(requestUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            
            // Use the native updateMedia method with the fetched HTML
            this.updateMedia(html, variantFeaturedMediaId);
          })
          .catch((error) => {
            console.error('Error updating media gallery:', error);
            
            // Fallback: just set the active media
            const mediaGallery = this.querySelector('media-gallery');
            if (mediaGallery && mediaGallery.setActiveMedia) {
              const mediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
              mediaGallery.setActiveMedia(mediaId, true);
            }
          });
      }

      handleSplitPickerUpdate(selectedVariant) {
        if (!selectedVariant) {
          this.setUnavailable();
          return;
        }

        // Update variant inputs
        this.updateVariantInputs(selectedVariant.id);
        
        // Update URL
        this.updateURL(this.dataset.url, selectedVariant.id);
        
        // Update media gallery
        this.updateMediaForSplitPicker(selectedVariant.featured_media?.id);
        
        // Update pickup availability
        this.pickupAvailability?.update(selectedVariant);

        // Fetch fresh section HTML to update price, inventory, SKU, etc.
        const requestUrl = `${this.dataset.url}?variant=${selectedVariant.id}&section_id=${this.sectionId}`;
        fetch(requestUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateElementsSelectively(html);
          })
          .catch((error) => {
            console.error('Error updating price for split picker:', error);
          });
        
        this.productForm?.setSubmitState(selectedVariant.available ? 'ready' : 'sold-out');
        
        // Publish variant change event
        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            variant: selectedVariant,
          },
        });
        
        // Reinitialize split variant logic
        if (window.initSplitVariantLogic) {
          window.initSplitVariantLogic();
        }
        if (window.initRegularVariantLogic) {
          window.initRegularVariantLogic();
        }
        if (window.initATCButtonStateManagement) {
          window.initATCButtonStateManagement();
        }
      }



      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
